import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@zillow/constellation-10';
import {
  useProofOfInsuranceWebApiHandler_getTenantsList,
  useProofOfInsuranceWebApiHandler_sendProofRequestEmail,
} from '@zg-rentals/ts-rins-client-react-query';
import { type ListingTenantInfoDto as Tenant } from '@zg-rentals/ts-rins-client';
import { Skeleton } from './Skeleton';
import { AddTenants } from './AddTenants';
import { SendRequest } from './SendRequest';

const TOAST_MESSAGE = {
  SEND_SUCCESS: 'The request has been sent to the tenant',
  SEND_ERROR: 'Failed to send request. Please try again.',
} as const;
type ProofRecomendedActionsProps = {
  listingAlias: string;
  isOpen: boolean;
  onSendRequestClick?: () => void;
  onAddTenantsClick: () => void;
  onSendRequestEmailSuccess?: (message: string) => void | null;
  onSendRequestEmailError?: (message: string) => void | null;
  onClose: () => void;
};

const ProofRecomendedActions = ({
  listingAlias,
  isOpen,
  onSendRequestClick,
  onAddTenantsClick,
  onSendRequestEmailSuccess,
  onSendRequestEmailError,
  onClose,
}: ProofRecomendedActionsProps) => {
  const [selectedTenants, setSelectedTenants] = useState<Record<string, Tenant>>({});

  const { data, isLoading, isFetching, refetch } = useProofOfInsuranceWebApiHandler_getTenantsList(
    {
      listingAlias,
    },
    {
      // Tenants list is updated by other components, so we need to refetch the tenants list
      gcTime: 0,
    },
  );

  // Refetch when modal opens to get latest tenant data
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  const { mutate: sendProofRequestEmail } = useProofOfInsuranceWebApiHandler_sendProofRequestEmail({
    onError: () => {
      onSendRequestEmailError?.(TOAST_MESSAGE.SEND_ERROR);
    },
    onSuccess: () => {
      onSendRequestEmailSuccess?.(TOAST_MESSAGE.SEND_SUCCESS);
    },
  });

  const tenantsList = useMemo(() => data?.tenants ?? [], [data]);
  const hasTenants = tenantsList.length > 0;

  // Show loading state during initial load or refetch
  const isLoadingData = isLoading || isFetching;

  const handleTenantChange = (tenant: Tenant) => {
    setSelectedTenants((prev) => {
      const newSelected = { ...prev };
      if (tenant.email in newSelected) {
        delete newSelected[tenant.email];
      } else {
        newSelected[tenant.email] = tenant;
      }
      return newSelected;
    });
  };

  const handleSendProofRequestEmail = () => {
    sendProofRequestEmail({
      postData: {
        listingAlias,
        tenants: Object.values(selectedTenants),
      },
    });
    onSendRequestClick?.();
    onClose();
  };

  // Determine modal state and get corresponding content
  let modalState;

  if (isLoadingData) {
    modalState = Skeleton();
  } else if (hasTenants) {
    modalState = SendRequest({
      tenantsList,
      selectedTenants,
      onTenantChange: handleTenantChange,
      onSendRequest: handleSendProofRequestEmail,
    });
  } else {
    modalState = AddTenants({
      onAddTenantsClick,
    });
  }

  const { header, body, footer } = modalState;

  return (
    <Modal
      open={isOpen}
      size="md"
      scrollDocument
      header={header}
      body={body}
      footer={footer}
      onOpenChange={isLoadingData ? undefined : onClose}
    />
  );
};

export default ProofRecomendedActions;
